import { createContext } from 'react';
import type { useProviderConnection } from '@/hooks/useProviderConnection';

/**
 * SyncConnectionContext - Global Provider Connection State
 *
 * Provides a single source of truth for provider connection state across the application.
 * This prevents divergent state bugs that occur when multiple components create separate
 * useProviderConnection hook instances.
 *
 * Architecture:
 * - Mounted in AppContent (after AuthProvider) with user.id
 * - All components use useSyncConnection() hook instead of useProviderConnection directly
 * - Supports multi-provider connections (Google Drive, Local Directory)
 *
 * @example
 * // In component:
 * const { state, connect, disconnect, abort } = useSyncConnection();
 */

/**
 * Context value type derived from useProviderConnection return type.
 * Using ReturnType ensures type safety and automatic sync with hook changes.
 */
export type SyncConnectionContextValue = ReturnType<typeof useProviderConnection>;

/**
 * Context with null default - requires provider to be mounted
 */
export const SyncConnectionContext = createContext<SyncConnectionContextValue | null>(null);
